import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlagSearch } from './FeatureFlagsContext';
import {
  QUEST_GLITCH_MS,
  createRandomGlitchFrame,
  glitchFrameToStyle,
  nextGlitchDelayMs,
  prefersReducedGlitchMotion,
} from './questGlitch';

type QuestGlitchContextValue = {
  isGlitching: boolean;
  glitchStyle: CSSProperties;
  startQuestTransition: (targetUrl?: string) => void;
};

const QuestGlitchContext = createContext<QuestGlitchContextValue | null>(null);

export function QuestGlitchProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const flagSearch = useFlagSearch();
  const [isGlitching, setIsGlitching] = useState(false);
  const [glitchStyle, setGlitchStyle] = useState<CSSProperties>({});
  const timeoutRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  const clearTransition = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (tickRef.current !== null) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }
    setGlitchStyle({});
    setIsGlitching(false);
  }, []);

  useEffect(() => {
    if (!isGlitching) {
      setGlitchStyle({});
      return undefined;
    }

    if (prefersReducedGlitchMotion()) {
      setGlitchStyle({ opacity: 0.9 });
      return undefined;
    }

    let cancelled = false;

    const scheduleTick = () => {
      if (cancelled) return;
      setGlitchStyle(glitchFrameToStyle(createRandomGlitchFrame()));
      tickRef.current = window.setTimeout(scheduleTick, nextGlitchDelayMs());
    };

    scheduleTick();

    return () => {
      cancelled = true;
      if (tickRef.current !== null) {
        window.clearTimeout(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isGlitching]);

  const startQuestTransition = useCallback((targetUrl?: string) => {
    if (isGlitching) return;

    const destination = targetUrl ?? '/quest/coeur';
    const isExternal = /^https?:\/\//.test(destination);

    setIsGlitching(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      if (tickRef.current !== null) {
        window.clearTimeout(tickRef.current);
        tickRef.current = null;
      }
      setGlitchStyle({});
      setIsGlitching(false);
      if (isExternal) {
        window.location.href = destination;
      } else {
        navigate({ pathname: destination, search: flagSearch });
      }
    }, QUEST_GLITCH_MS);
  }, [flagSearch, isGlitching, navigate]);

  useEffect(() => {
    document.body.classList.toggle('page-glitch-active', isGlitching);
    return () => {
      document.body.classList.remove('page-glitch-active');
    };
  }, [isGlitching]);

  useEffect(() => clearTransition, [clearTransition]);

  return (
    <QuestGlitchContext.Provider value={{ isGlitching, glitchStyle, startQuestTransition }}>
      {children}
    </QuestGlitchContext.Provider>
  );
}

export function useQuestGlitch(): QuestGlitchContextValue {
  const context = useContext(QuestGlitchContext);
  if (!context) {
    throw new Error('useQuestGlitch doit être utilisé sous QuestGlitchProvider');
  }
  return context;
}
